import enAU from "date-fns/locale/en-AU"; // the locale you want
import React, { ChangeEvent, FocusEvent, useCallback, useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import register from "../../registerServiceWorker";
import { validationMessageContent } from "../../utils/ComponentUtils";
import { getInputClassName } from "../../utils/utils";
import { ITextInputProps } from "../types/TextInput.interfaces";
import { InputCssClasses } from "./InputCssClasses";
import { ValidationMessages } from "./ValidationMessages";

const TextInput: React.FC<ITextInputProps> = (props) => {
    const [touched, setTouch] = useState(props.touched);
    const [error, setError] = useState("");
    const [htmlClass, setHtmlClass] = useState("");
    const [, setValue] = useState("");
    const [className, setClassName] = useState(getInputClassName(touched, props.inputClass, htmlClass));

    // Input Types.
    const textAreaType: string = "textarea";
    const isTextAreaInput: boolean = props.type?.toLowerCase() === textAreaType;
    const datePickerType: string = "datepicker";

    // Messages.
    const valueIsRequired: string = ValidationMessages.ValueIsRequired;
    const validCssClass: string = InputCssClasses.ValidCssClass;
    const invalidCssClass: string = InputCssClasses.InvalidCssClass;

    // Used for referencing the input fields
    const textInputRef = useRef<HTMLInputElement>();
    const textAreaInputRef = useRef<HTMLTextAreaElement>();

    const setFocus = useCallback((): void => {
        if (isTextAreaInput) {
            textAreaInputRef.current?.focus();
        }
        else {
            textInputRef.current?.focus();
        }
    }, [isTextAreaInput]);

    useEffect(() => {
        // Autofocus the element.
        if (props.autofocus) {
            setTimeout(setFocus, 500);
        }
    }, [props.autofocus, setFocus]);

    useEffect(() => {
        if (!props.touched) {
            setHtmlClass("");
            setClassName(getInputClassName(props.touched, props.inputClass, ""));
        }
    }, [props.touched, props.inputClass]);

    function onTextAreaBlur(event: FocusEvent<HTMLTextAreaElement>): void {
        onBlur(event.target.value);
    }

    function onTextInputBlur(event: FocusEvent<HTMLInputElement>): void {
        onBlur(event.target.value);
    }
    
    function onBlur(elementValue: string): void {
        // Validate the control has a value set if required. Only check if there is no current error.
        if (!error) {
            checkIfRequiredField(elementValue);
        }
    }

    function checkIfRequiredField(elementValue: string): [string, string] {
        let [currentError, validClass] = ["", ""];

        // Check that a value has been entered and that it doesn't contain all spaces.
        [currentError, validClass] = ((!elementValue || elementValue.length === 0 || elementValue.trim().length === 0) && props.required) ? [valueIsRequired, invalidCssClass] : ["", validCssClass];

        return [currentError, validClass];
    }

    function onTextAreaValueChanged(event: ChangeEvent<HTMLTextAreaElement>): void {
        onValueChanged(event.target.value);
    }

    function onInputValueChanged(event: ChangeEvent<HTMLInputElement>): void {
        onValueChanged(event.target.value);
    }

    function onDatePickerChanged(date: Date): void {
        var elementValue = date ? date.toISOString() : "";

        onValueChanged(elementValue);
    }

    function onValueChanged(elementValue: string): void {
        let [currentError, validClass] = checkIfRequiredField(elementValue);

        if (!currentError) {
            [currentError, validClass] = (props.maxLength && elementValue && elementValue.length > (props.maxLength)) ?
                [`Value can't have more than ${props.maxLength} characters`, invalidCssClass] : ["", validCssClass];
        }

        props.onChange({ value: elementValue, error: currentError, touched: true, field: props.field });

        setTouch(true);
        setError(currentError);
        setHtmlClass(validClass);
        setValue(elementValue);
        setClassName(getInputClassName(true, props.inputClass, validClass));
    }

    const textAreaContent = (): JSX.Element => {
        return (
            <textarea
                value={props.value}
                onChange={onTextAreaValueChanged}
                className={className}
                id={props.id}
                placeholder={props.placeholder}
                rows={props.rows}
                onBlur={onTextAreaBlur}
                ref={(e: HTMLTextAreaElement) => { register(); textAreaInputRef.current = e; }}
            >
            </textarea>
        );
    } 

    const textInputContent = (): JSX.Element => {
        return (
            <input
                value={props.value}
                type={props.type}
                onChange={onInputValueChanged}
                className={className}
                id={props.id}
                placeholder={props.placeholder}
                onBlur={onTextInputBlur}
                ref={(e: HTMLInputElement) => { register(); textInputRef.current = e; }}
            />
        );
    }

    // See DatePicker settings here: https://reactdatepicker.com/?spm=a2c6h.14275010.0.0.13922af1bXcAUb
    const datePickerContent = (): JSX.Element => {
        const dateFormat: string = "dd/MM/yyyy";
        let isInitialDateValid: boolean = props.value?.length > 0;
        let initialDate: Date | null = isInitialDateValid ? new Date(props.value) : null;

        // Highlight today in the calendar.
        let today: Date = new Date();
        let highlightDates: Date[] = [today];

        return (
            <DatePicker
                id={props.id}
                locale={enAU}
                showPopperArrow={false}
                selected={initialDate}
                onChange={onDatePickerChanged}
                className={className}
                placeholderText={props.placeholder}
                highlightDates={highlightDates}
                dateFormat={dateFormat}
                isClearable={props.isClearable}
            />
        );
    }

    const getInputContent = (): JSX.Element => {
        let element: JSX.Element;

        switch (props.type) {
            case textAreaType: {
                element = textAreaContent();
                break;
            }
            case datePickerType: {
                element = datePickerContent();
                break;
            }
            default: {
                element = textInputContent();
                break;
            }
        }

        return element;
    }

    return (
        <div>
            {props.hideLabel ? null : <label htmlFor={props.id.toString()}>{props.label}{props.required ? <small> (required)</small> : ""}</label>}
            {getInputContent()}
            {validationMessageContent(error)}
        </div>
    );
}

export default TextInput;